import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { MilaMultiSelectHierarchyFetcher, MilaMultiSelectOption } from '../mila-multi-select/mila-multi-select-option.model';

/**
 * Same combobox shell as MilaMultiSelect (chips + search field + dropdown),
 * but the dropdown renders a tree of MilaMultiSelectTreeItem instead of a
 * flat list each root node lazy-loads its own children on expand.
 *
 * Deliberately does NOT hide already-selected nodes from the tree the way
 * MilaMultiSelect hides them from its flat list removing a selected node
 * from an expanded branch would collapse/reshuffle the tree under the
 * user's cursor. Selected nodes just get a visual "already picked" state
 * instead (see MilaMultiSelectTreeItem's `--selected` class).
 */
@Component({
  selector: 'mila-multi-select-hierarchy',
  standalone: false,
  templateUrl: './mila-multi-select-hierarchy.html',
  styleUrl: './mila-multi-select-hierarchy.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MilaMultiSelectHierarchy),
      multi: true,
    },
  ],
})
export class MilaMultiSelectHierarchy implements ControlValueAccessor, OnInit, OnDestroy {
  @Input({ required: true }) fetchPage!: MilaMultiSelectHierarchyFetcher;
  @Input() placeholder = '';
  /** null = unlimited. Ignored when multiple is false. */
  @Input() maxSelected: number | null = null;
  /** false = picking a second node replaces the first instead of adding
   *  to the selection (and instead of blocking on maxSelected). */
  @Input() multiple = true;

  private readonly _destroy$ = new Subject<void>();
  private readonly _query$ = new Subject<string>();

  selected: MilaMultiSelectOption[] = [];
  /** Root-level nodes only nested children live inside each tree item. */
  rootOptions: MilaMultiSelectOption[] = [];
  queryText = '';
  isOpen = false;
  isLoading = false;
  limitMessage = '';

  private disabled = false;

  private onChange: (value: MilaMultiSelectOption[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this._query$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe(query => this.loadRoot(query));
  }

  // ── ControlValueAccessor ────────────────────────────────────────────
  writeValue(value: MilaMultiSelectOption[] | null): void {
    this.selected = value ?? [];
  }

  registerOnChange(fn: (value: MilaMultiSelectOption[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get selectedIds(): Set<MilaMultiSelectOption['id']> {
    return new Set(this.selected.map(o => o.id));
  }

  // ── Open / close ─────────────────────────────────────────────────────
  open(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    if (!this.rootOptions.length) this.loadRoot('');
  }

  /** Bound to (appClickOutside) on the component's own root in the template
   *  safe to call even when already closed. */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
  }

  // ── Search (root level only a matched node's own children still load lazily) ──
  onInput(value: string): void {
    this.queryText = value;
    // Typing always shows the panel don't rely solely on the earlier
    // (focus) event having left isOpen true; whatever else could flip it,
    // actively typing into the field should never leave the list hidden.
    if (!this.isOpen && !this.disabled) {
      this.isOpen = true;
    }
    this._query$.next(value);
  }

  private loadRoot(query: string): void {
    this.isLoading = true;
    this.fetchPage(query, 0, null)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: res => {
          this.rootOptions = res.items;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  // ── Selection ────────────────────────────────────────────────────────
  onTreeSelect(option: MilaMultiSelectOption): void {
    if (this.selected.some(o => o.id === option.id)) return;

    if (!this.multiple) {
      // Single-select: picking one swaps out whatever was there, never
      // blocked by maxSelected (that's a multi-select-only concept).
      this.limitMessage = '';
      this.selected = [option];
      this.emit();
      this.resetSearchIfAny();
      return;
    }

    if (this.maxSelected != null && this.selected.length >= this.maxSelected) {
      this.limitMessage = `You can add up to ${this.maxSelected} items.`;
      return;
    }

    this.limitMessage = '';
    this.selected = [...this.selected, option];
    this.emit();
    this.resetSearchIfAny();
  }

  /** Same reasoning as MilaMultiSelect clear the search back to the
   *  unfiltered root list after a pick, instead of leaving stale search
   *  text (and a list still filtered to it) sitting there. */
  private resetSearchIfAny(): void {
    if (this.queryText) {
      this.queryText = '';
      this.loadRoot('');
    }
  }

  remove(option: MilaMultiSelectOption, event?: Event): void {
    event?.stopPropagation();
    this.selected = this.selected.filter(o => o.id !== option.id);
    this.limitMessage = '';
    this.emit();
  }

  private emit(): void {
    this.onChange(this.selected);
    this.onTouched();
  }

  // ── Keyboard ─────────────────────────────────────────────────────────
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.queryText && this.selected.length) {
      this.remove(this.selected[this.selected.length - 1]);
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
